/**
 * Layer B of the token architecture (docs/design-system/03-TOKENS-COLOR-TYPOGRAPHY.md): one
 * namespace per brand. This file is the runtime source of truth for which master asset each brand
 * uses, its stage tone and its accent; the matching CSS custom properties live in
 * src/styles/brands.css. Provenance for every value is recorded in
 * docs/design-system/09-BRAND-ASSET-REGISTER.md.
 *
 * Status vocabulary:
 *   confirmed            value taken from an owner-approved source (guideline PDF, official website, master file)
 *   derived              presentation-only value derived from a confirmed one (e.g. a tint for dark stages); never presented as official
 *   pending-approval     MINT branch identity: proposals exist, nothing may ship until the owner approves each branch
 */

export type BrandId =
  | 'moderta'
  | 'mint'
  | 'mint-it'
  | 'mint-travel'
  | 'mint-business'
  | 'research-space'
  | 'native'
  | 'carbonmove'
  | 'climateviewpoint'
  | 'etrust';

export type BrandStatus = 'confirmed' | 'pending-approval';
export type StageTone = 'dark' | 'light';

export interface LogoAsset {
  /** Path under public/, or 'spec' for a mark the guideline defines by construction (Research Space). */
  src: string;
  /** Intrinsic width / height, so lockups never distort. */
  ratio: number;
  alt: string;
}

export interface Brand {
  id: BrandId;
  name: string;
  status: BrandStatus;
  /** Which stage this brand's scenes are art-directed for. Chrome and particles follow it. */
  tone: StageTone;
  /** The logo variant for this brand's own stage. */
  logo: LogoAsset;
  /** The variant used on the dark Moderta stage (ecosystem map, closing). */
  logoOnDark: LogoAsset;
  /** Brand accent used for particles and the active progress marker while the brand's scene is on screen. */
  accent: string;
  /** Short source note; the full register is in docs/design-system/09-BRAND-ASSET-REGISTER.md. */
  source: string;
}

const MINT_LOGO: LogoAsset = { src: '/branding/mint/mint-square-dot-logo.svg', ratio: 82 / 22, alt: 'MINT' };

/**
 * MINT branches deliberately reuse the MINT parent identity until the owner approves a branch's
 * logo, palette and typography (docs/design-system/proposals/MINT-BRANCH-PROPOSALS.md).
 */
const mintBranch = (id: BrandId, name: string): Brand => ({
  id,
  name,
  status: 'pending-approval',
  tone: 'light',
  logo: MINT_LOGO,
  logoOnDark: MINT_LOGO,
  accent: '#257D5B',
  source: 'Pending owner approval. Uses the MINT master SVG and MINT parent stage tokens only.',
});

export const BRANDS: Record<BrandId, Brand> = {
  moderta: {
    id: 'moderta',
    name: 'Moderta',
    status: 'confirmed',
    tone: 'dark',
    logo: { src: '/branding/moderta/moderta-logo-white.png', ratio: 943 / 274, alt: 'Moderta' },
    logoOnDark: { src: '/branding/moderta/moderta-logo-white.png', ratio: 943 / 274, alt: 'Moderta' },
    accent: '#67B5AC', // derived: #00796B mixed 45% toward #E6FFFC for legibility on the dark stage
    source: 'https://moderta.com/ — logo moderta_t_logo.png; palette --vamtam-accent-color-1…6; font Inter Tight.',
  },
  mint: {
    id: 'mint',
    name: 'MINT',
    status: 'confirmed',
    tone: 'light',
    logo: MINT_LOGO,
    logoOnDark: MINT_LOGO,
    accent: '#257D5B',
    source: 'public/branding/mint/mint-square-dot-logo.svg (owner-supplied master, fill #257D5B).',
  },
  'mint-it': mintBranch('mint-it', 'MINT-IT'),
  'mint-travel': mintBranch('mint-travel', 'MINT Travel & Events'),
  'mint-business': mintBranch('mint-business', 'MINT Business'),
  'research-space': {
    id: 'research-space',
    name: 'Moderta AI — Research Space',
    status: 'confirmed',
    tone: 'light',
    logo: { src: 'spec', ratio: 1, alt: 'Moderta AI — Research Space' },
    logoOnDark: { src: 'spec', ratio: 1, alt: 'Moderta AI — Research Space' },
    accent: '#546E7A',
    source: 'Moderta-AI-Research-Space-Branding-and-UIUX.pdf pp. 2–4 (monogram construction, palette, type roles).',
  },
  native: {
    id: 'native',
    name: 'Moderta Native',
    status: 'confirmed',
    tone: 'dark',
    logo: { src: '/branding/native/moderta-native-logo-reversed.svg', ratio: 142.173 / 42.304, alt: 'Moderta Native' },
    logoOnDark: { src: '/branding/native/moderta-native-logo-reversed.svg', ratio: 142.173 / 42.304, alt: 'Moderta Native' },
    accent: '#1783C1',
    source: 'Moderta-Native-Brand-Guidelines.pdf p. 5 (vector master, extracted unmodified), p. 9 palette, p. 8 Poppins.',
  },
  carbonmove: {
    id: 'carbonmove',
    name: 'CarbonMove',
    status: 'confirmed',
    tone: 'dark',
    logo: { src: '/branding/carbonmove/carbonmove-logo.svg', ratio: 175 / 35, alt: 'CarbonMove' },
    logoOnDark: { src: '/branding/carbonmove/carbonmove-logo.svg', ratio: 175 / 35, alt: 'CarbonMove' },
    accent: '#05D36E',
    source: 'https://carbonmove.io/ — LOGO.svg; palette from site CSS (--color-emerald-green, --color-lime-glow …).',
  },
  climateviewpoint: {
    id: 'climateviewpoint',
    name: 'ClimateViewPoint',
    status: 'confirmed',
    tone: 'dark',
    logo: { src: '/branding/climateviewpoint/climateviewpoint-logo-reverse.png', ratio: 1223 / 202, alt: 'ClimateViewPoint' },
    logoOnDark: { src: '/branding/climateviewpoint/climateviewpoint-logo-reverse.png', ratio: 1223 / 202, alt: 'ClimateViewPoint' },
    accent: '#5DC7AC',
    source: 'ClimateViewPoint-Brand-Guidelines.pdf p. 16 (reverse master on Understory), pp. 19–21 palette, p. 26 type.',
  },
  etrust: {
    id: 'etrust',
    name: 'eTrust',
    status: 'confirmed',
    tone: 'light',
    logo: { src: '/branding/etrust/etrust-logo-blue.webp', ratio: 176 / 46, alt: 'eTrust' },
    logoOnDark: { src: '/branding/etrust/etrust-logo-white.png', ratio: 176 / 46, alt: 'eTrust' },
    accent: '#006BC7',
    source: 'https://etrust.lk/ — eTrustBlue / eTrustWhite (176×46 raster, vector master requested); palette from site CSS; font Nunito.',
  },
};

export const brand = (id: BrandId): Brand => BRANDS[id];
